import React, { useRef } from 'react';
import Modal from './Modal';
import Button from './Button';

const TONES = {
  danger: { variant: 'danger', defaultIcon: '⚠️' },
  approve: { variant: 'approve', defaultIcon: '✅' },
  primary: { variant: 'primary', defaultIcon: null },
};

/**
 * Confirmation dialog composed on top of Modal. Focuses the cancel button by default so
 * a stray Enter keypress doesn't trigger a destructive action.
 *
 * Props:
 *  - tone: 'danger' | 'approve' | 'primary'
 *  - title, description
 *  - confirmLabel, cancelLabel
 *  - onConfirm, onClose
 *  - loading: disables actions and shows a spinner on confirm
 *  - icon: overrides the tone's default icon (pass null to hide)
 */
export default function ConfirmDialog({
  isOpen,
  tone = 'primary',
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onClose,
  loading = false,
  icon,
}) {
  const cancelRef = useRef(null);
  const toneConfig = TONES[tone] || TONES.primary;
  const resolvedIcon = icon === undefined ? toneConfig.defaultIcon : icon;

  // Pass the title (with icon) to Modal so it becomes the dialog's accessible name.
  const titleNode = (
    <span className="flex items-center gap-2">
      {resolvedIcon && <span aria-hidden="true">{resolvedIcon}</span>}
      <span>{title}</span>
    </span>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={title ? titleNode : undefined}
      hideCloseButton
      initialFocusRef={cancelRef}
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
      footer={(
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={toneConfig.variant} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      )}
    >
      {description && <p className="text-gray-600">{description}</p>}
    </Modal>
  );
}
